import { useState, useEffect, useRef, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { db } from "~/db/db";
import {
  apiBackOffice,
  apiCadastro,
  apiSimulacao,
  apiMapaMina,
  apiTabelaPreco,
  apiFunilObra,
  apiRoteirizacao,
} from "~/services/utils/api";

const SYNC_INTERVAL = 60000;
const MAX_RETRIES = 3;
const MIN_CONNECTION_SPEED = 0.1;

const apiMap = {
  backOffice: apiBackOffice,
  cadastro: apiCadastro,
  simulacao: apiSimulacao,
  mapaMina: apiMapaMina,
  tabelaPreco: apiTabelaPreco,
  funilObra: apiFunilObra,
  roteirizacao: apiRoteirizacao,
};

export const useSyncOfflineMonitor = () => {
  const [syncState, setSyncState] = useState({
    isProcessing: false,
    currentRequest: null,
    progress: { current: 0, total: 0 },
    message: "",
    lastSync: null,
    errors: [],
  });

  const mountedRef = useRef(true);
  const toast = useToast();
  const syncIntervalRef = useRef(null);
  const isProcessingRef = useRef(false);
  const tableSyncActiveRef = useRef(false);
  const pendingSyncAfterTablesRef = useRef(false);

  const getNetworkConnection = useCallback(() => {
    return (
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection || {
        downlink: 1,
        effectiveType: "4g",
      }
    );
  }, []);

  const isOnlineWithGoodConnection = useCallback(async () => {
    if (!navigator.onLine) return false;
    const connection = getNetworkConnection();
    return connection?.downlink > MIN_CONNECTION_SPEED;
  }, [getNetworkConnection]);

  const checkConnectivity = useCallback(async () => {
    if (!navigator.onLine) {
      return { isOnline: false, hasGoodSpeed: false, responseTime: 0 };
    }

    const hasGoodConnection = await isOnlineWithGoodConnection();

    if (!hasGoodConnection) {
      return { isOnline: false, hasGoodSpeed: false, responseTime: 0 };
    }

    try {
      const start = Date.now();
      await fetch("https://www.google.com/favicon.ico", {
        method: "HEAD",
        mode: "no-cors",
        cache: "no-cache",
        signal: AbortSignal.timeout(5000),
      });
      const end = Date.now();
      const responseTime = end - start;

      return {
        isOnline: true,
        hasGoodSpeed: true,
        responseTime,
      };
    } catch (error) {
      try {
        const start = Date.now();
        await fetch("data:text/plain;base64,", {
          method: "GET",
          cache: "no-cache",
          signal: AbortSignal.timeout(3000),
        });
        const end = Date.now();

        return {
          isOnline: true,
          hasGoodSpeed: true,
          responseTime: end - start,
        };
      } catch (fallbackError) {
        return { isOnline: false, hasGoodSpeed: false, responseTime: 0 };
      }
    }
  }, [isOnlineWithGoodConnection]);

  const syncSingleRequest = useCallback(async (request) => {
    try {
      const api = apiMap[request.apiName];
      if (!api) {
        throw new Error(`API ${request.apiName} não encontrada`);
      }

      let response;

      switch (request.method.toLowerCase()) {
        case "post":
          response = await api.post(request.endpoint, request.data);
          break;
        case "put":
          response = await api.put(request.endpoint, request.data);
          break;
        case "patch":
          response = await api.patch(request.endpoint, request.data);
          break;
        case "delete":
          response = await api.delete(request.endpoint);
          break;
        default:
          throw new Error(`Método ${request.method} não suportado para sincronização`);
      }

      if (response?.isSuccess || (response?.status >= 200 && response?.status < 300)) {
        return { success: true, data: response };
      } else {
        throw new Error(`Resposta inválida: ${response?.status || "Desconhecido"}`);
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const processPendingRequests = useCallback(async () => {
    if (isProcessingRef.current) {
      return;
    }

    if (tableSyncActiveRef.current) {
      pendingSyncAfterTablesRef.current = true;
      return;
    }

    try {
      const pendingCount = await db.pendingRequests.count();

      if (pendingCount === 0) {
        return;
      }

      const connectivity = await checkConnectivity();

      if (!connectivity.isOnline || !connectivity.hasGoodSpeed) {
        return;
      }

      isProcessingRef.current = true;

      if (!mountedRef.current) return;

      const pendingRequests = await db.pendingRequests.toArray();

      setSyncState((prev) => ({
        ...prev,
        isProcessing: true,
        progress: { current: 0, total: pendingRequests.length },
        message: `Sincronizando 0 de ${pendingRequests.length}...`,
        errors: [],
        currentRequest: null,
      }));

      let successCount = 0;
      let errorCount = 0;
      const failedRequests = [];

      for (let i = 0; i < pendingRequests.length; i++) {
        const request = pendingRequests[i];

        if (!mountedRef.current) break;

        setSyncState((prev) => ({
          ...prev,
          currentRequest: request.endpoint,
          progress: { current: i + 1, total: pendingRequests.length },
          message: `Sincronizando ${i + 1} de ${pendingRequests.length}...`,
        }));

        try {
          const result = await syncSingleRequest(request);

          if (result.success) {
            successCount++;
            await db.pendingRequests.delete(request.id);
          } else {
            throw new Error(result.error);
          }
        } catch (error) {
          errorCount++;

          const retryCount = (request.retryCount || 0) + 1;

          if (retryCount >= MAX_RETRIES) {
            await db.pendingRequests.delete(request.id);
            failedRequests.push({ ...request, error: error.message });
          } else {
            await db.pendingRequests.update(request.id, {
              retryCount,
              lastError: error.message,
              lastAttempt: new Date().toISOString(),
            });
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      if (!mountedRef.current) return;

      setSyncState((prev) => ({
        ...prev,
        isProcessing: false,
        currentRequest: null,
        lastSync: new Date(),
        message: `Sincronização concluída: ${successCount} sucessos, ${errorCount} erros`,
        errors: failedRequests,
      }));

      if (successCount > 0) {
        toast({
          title: `${successCount} de ${pendingRequests.length} sincronizados com sucesso`,
          description: "Dados offline enviados ao servidor",
          status: "success",
          duration: 4000,
          isClosable: true,
          position: "top-right",
        });
      }

      if (errorCount > 0) {
        const failedAfterRetries = failedRequests.length;
        const title =
          failedAfterRetries > 0
            ? `${failedAfterRetries} requisições falharam após ${MAX_RETRIES} tentativas`
            : `${errorCount} requisições com erro temporário`;

        const description =
          failedAfterRetries > 0
            ? "Algumas requisições foram descartadas por excesso de falhas"
            : "Tentaremos novamente na próxima sincronização";

        toast({
          title,
          description,
          status: "error",
          duration: 6000,
          isClosable: true,
          position: "top-right",
        });
      }
    } catch (error) {
      if (!mountedRef.current) return;

      setSyncState((prev) => ({
        ...prev,
        isProcessing: false,
        currentRequest: null,
        message: "Erro durante a sincronização",
        errors: [{ general: error.message }],
      }));
    } finally {
      isProcessingRef.current = false;
    }
  }, [checkConnectivity, syncSingleRequest, toast]);

  const startSyncLoop = useCallback(() => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
    }

    processPendingRequests();

    syncIntervalRef.current = setInterval(() => {
      processPendingRequests();
    }, SYNC_INTERVAL);
  }, [processPendingRequests]);

  const stopSyncLoop = useCallback(() => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }
  }, []);

  const forceSync = useCallback(async () => {
    if (isProcessingRef.current) {
      return false;
    }

    await processPendingRequests();
    return true;
  }, [processPendingRequests]);

  const forceSyncIgnoringTables = useCallback(async () => {
    if (isProcessingRef.current) {
      return false;
    }

    const originalTableSyncActive = tableSyncActiveRef.current;
    tableSyncActiveRef.current = false;

    try {
      await processPendingRequests();
      return true;
    } finally {
      tableSyncActiveRef.current = originalTableSyncActive;
    }
  }, [processPendingRequests]);

  const getSyncStatus = useCallback(() => {
    return {
      isProcessing: isProcessingRef.current,
      hasInterval: !!syncIntervalRef.current,
      intervalTime: SYNC_INTERVAL,
    };
  }, []);

  const stopSync = useCallback(() => {
    stopSyncLoop();
    setSyncState((prev) => ({
      ...prev,
      isProcessing: false,
      currentRequest: null,
      message: "Sincronização pausada",
    }));
  }, [stopSyncLoop]);

  const setTableSyncActive = useCallback(
    (isActive) => {
      const wasActive = tableSyncActiveRef.current;
      tableSyncActiveRef.current = isActive;

      if (wasActive && !isActive) {
        setTimeout(async () => {
          const pendingCount = await db.pendingRequests.count();

          if (pendingCount > 0) {
            processPendingRequests();
          }

          pendingSyncAfterTablesRef.current = false;
        }, 1000);
      }
    },
    [processPendingRequests]
  );

  useEffect(() => {
    startSyncLoop();

    return () => {
      mountedRef.current = false;
      stopSyncLoop();
    };
  }, [startSyncLoop, stopSyncLoop]);

  useEffect(() => {
    const handleOnline = async () => {
      const attemptSync = async (attempt = 1, maxAttempts = 3) => {
        const connectivity = await checkConnectivity();

        if (connectivity.isOnline && connectivity.hasGoodSpeed) {
          await processPendingRequests();
          return true;
        } else if (attempt < maxAttempts) {
          setTimeout(() => attemptSync(attempt + 1, maxAttempts), attempt * 3000);
          return false;
        } else {
          return false;
        }
      };

      setTimeout(() => attemptSync(), 2000);
    };

    const handleOffline = () => {};

    globalThis.addEventListener("online", handleOnline);
    globalThis.addEventListener("offline", handleOffline);

    return () => {
      globalThis.removeEventListener("online", handleOnline);
      globalThis.removeEventListener("offline", handleOffline);
    };
  }, [checkConnectivity, processPendingRequests]);

  return {
    ...syncState,
    forceSync,
    forceSyncIgnoringTables,
    getSyncStatus,
    stopSync,
    setTableSyncActive,
    restartSync: startSyncLoop,
    progressPercentage:
      syncState.progress.total > 0 ? Math.round((syncState.progress.current / syncState.progress.total) * 100) : 0,
    hasErrors: syncState.errors.length > 0,
    isIdle: !syncState.isProcessing,
    lastSyncFormatted: syncState.lastSync ? syncState.lastSync.toLocaleTimeString("pt-BR") : null,
    isTableSyncActive: tableSyncActiveRef.current,
  };
};

export default useSyncOfflineMonitor;
